import type { Drawer as DrawerPrimitive } from "vaul";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { TButtonProps } from "@/ui/components/primitives";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const DRAWER_CLOSE_ICON: TIconKey = "cross";

export const DRAWER_CONTENT_CLASS =
  "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[80vh] flex-col gap-4 rounded-t-sys-md border-t border-solid border-dialog bg-surface-default p-6 shadow-lg outline-none";

export const DRAWER_CONTAINER_CLASS = "flex flex-col";

export const DRAWER_OVERLAY_CLASS =
  "fixed inset-0 z-40 bg-sys-opacity-dark-20";

export const DRAWER_HEADER_CLASS = "flex w-full shrink-0 flex-col gap-2 text-center";

export const DRAWER_FOOTER_CLASS = "mt-auto flex shrink-0 flex-col gap-2";

export const DRAWER_CLOSE_CLASS =
  "absolute right-4 top-4 h-6 w-6 rounded-sys-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-sys-chartreuse-600 disabled:pointer-events-none";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TDrawerFooterCtas = {
  primary?: TButtonProps;
  secondary?: TButtonProps;
};

export type TDrawerRootProps = Omit<
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Root>,
  "direction"
> &
  PropsWithChildren & {
    title: string;
    description?: string;
    footer?: TDrawerFooterProps;
  };

export type TDrawerTriggerElement = ComponentRef<typeof DrawerPrimitive.Trigger>;
export type TDrawerTriggerProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Trigger>;
export type TDrawerOverlayElement = ComponentRef<typeof DrawerPrimitive.Overlay>;
export type TDrawerOverlayProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>;
export type TDrawerContentElement = ComponentRef<typeof DrawerPrimitive.Content>;
export type TDrawerContentProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>;
export type TDrawerCloseElement = ComponentRef<typeof DrawerPrimitive.Close>;
export type TDrawerCloseProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>;
export type TDrawerTitleElement = ComponentRef<typeof DrawerPrimitive.Title>;
export type TDrawerTitleProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>;
export type TDrawerDescriptionElement = ComponentRef<typeof DrawerPrimitive.Description>;
export type TDrawerDescriptionProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>;
export type TDrawerHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TDrawerFooterProps = HTMLAttributes<HTMLDivElement> & {
  ctas?: TDrawerFooterCtas;
};
